import {
  FormatSettings,
  MAX_DECIMAL_PLACES,
  MIN_DECIMAL_PLACES,
} from '@/types/format-settings'

/**
 * Core de formateo: puro, sin Vue, sin Firebase, sin Intl.
 *
 * Recibe FormatSettings explícitamente en vez de leer estado global. Eso permite:
 *  - testear sin montar nada,
 *  - alimentar el preview en vivo de la UI con valores pendientes sin comprometerlos,
 *  - usarlo desde PDF/Excel/stores (fuera de setup()).
 *
 * El wrapper reactivo vive en @/composables/useFormat.
 */

export type FormatIntent = 'money' | 'rate' | 'quantity' | 'integer' | 'percent' | 'weight'

export interface NumberFormatOptions {
  /** Qué representa el número. Determina los decimales salvo override. Default: 'money'. */
  intent?: FormatIntent
  /** Override absoluto de decimales. Gana sobre todo lo demás. */
  decimals?: number
  minDecimals?: number
  /** Los ceros finales se recortan hasta minDecimals. */
  maxDecimals?: number
  /** Separador de miles. Default: true. */
  grouping?: boolean
  /** Qué devolver ante null/undefined/inválido. Default: '-'. */
  fallback?: string
}

export interface CurrencyFormatOptions extends NumberFormatOptions {
  currency?: string
}

export interface DateFormatOptions {
  fallback?: string
}

export interface DateTimeFormatOptions extends DateFormatOptions {
  seconds?: boolean
}

export type FirestoreTimestampLike = { seconds: number; nanoseconds: number }
export type DateLike = string | number | Date | FirestoreTimestampLike | null | undefined

const DEFAULT_FALLBACK = '-'

/**
 * Decimales por intent.
 *
 * 'money' y 'rate' siguen settings.decimalPlaces: una tasa de cambio se lee junto
 * a los montos que convierte, así que con 4 decimales fijos mostraba relleno
 * ("1,0000") donde la compañía configuró 2. Los demás intents quedan inmunes al
 * global porque su precisión es propia del dato (pesos, porcentajes, cantidades).
 */
interface IntentDecimals {
  min: number
  max: number
}

const INTENT_DECIMALS: Record<Exclude<FormatIntent, 'money' | 'rate'>, IntentDecimals> = {
  quantity: { min: 0, max: 3 },
  weight: { min: 2, max: 2 },
  percent: { min: 2, max: 2 },
  integer: { min: 0, max: 0 },
}

const clampDecimals = (n: number): number =>
  Math.min(MAX_DECIMAL_PLACES, Math.max(MIN_DECIMAL_PLACES, Math.trunc(n)))

/**
 * Precedencia: decimals > min/maxDecimals > preset de intent > settings.decimalPlaces.
 */
function resolveDecimals(settings: FormatSettings, opts?: NumberFormatOptions): IntentDecimals {
  if (opts?.decimals !== undefined) {
    const d = clampDecimals(opts.decimals)
    return { min: d, max: d }
  }

  const intent = opts?.intent ?? 'money'
  const preset: IntentDecimals =
    intent === 'money' || intent === 'rate'
      ? { min: settings.decimalPlaces, max: settings.decimalPlaces }
      : INTENT_DECIMALS[intent]

  const min = opts?.minDecimals !== undefined ? clampDecimals(opts.minDecimals) : clampDecimals(preset.min)
  const max = opts?.maxDecimals !== undefined ? clampDecimals(opts.maxDecimals) : clampDecimals(preset.max)

  return max < min ? { min, max: min } : { min, max }
}

/** Acepta number, o string ya en formato máquina ('1234.56'). No parsea formatos localizados. */
function toNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const n = Number(String(value).trim())
  return Number.isFinite(n) ? n : null
}

/**
 * Redondea y rellena a `decimals`, replicando el redondeo de Intl.NumberFormat
 * (half-expand sobre la representación decimal).
 *
 * `toFixed` no sirve solo: redondea sobre el valor binario exacto, así que
 * (2.005).toFixed(2) === '2.00' mientras Intl daba '2,01' (2.005 se almacena
 * como 2.00499999...). Como los formatters anteriores usaban Intl, usar toFixed
 * directo sería una regresión silenciosa en montos de dinero.
 *
 * El truco: correr el punto decimal vía notación exponencial en el STRING, lo
 * que reparsea desde decimal y evita el error binario.
 */
export function toFixedHalfExpand(abs: number, decimals: number): string {
  const asString = String(abs)

  // Ya en notación exponencial (muy grande/pequeño): el shift no aplica
  if (asString.includes('e') || asString.includes('E')) return abs.toFixed(decimals)

  const shifted = Number(`${asString}e${decimals}`)
  if (!Number.isFinite(shifted)) return abs.toFixed(decimals)

  // abs siempre es positivo, así que Math.round (half-up) == half-expand
  const unshifted = Number(`${Math.round(shifted)}e-${decimals}`)
  if (!Number.isFinite(unshifted)) return abs.toFixed(decimals)

  return unshifted.toFixed(decimals)
}

function groupInteger(digits: string, separator: string): string {
  if (!separator) return digits
  // digits son solo dígitos (viene de toFixed sobre un abs), así que \B es seguro
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator)
}

/** Recorta ceros finales hasta dejar al menos minDecimals. */
function trimFraction(frac: string, minDecimals: number): string {
  let end = frac.length
  while (end > minDecimals && frac[end - 1] === '0') end--
  return frac.slice(0, end)
}

export function formatNumber(
  value: number | string | null | undefined,
  settings: FormatSettings,
  opts?: NumberFormatOptions
): string {
  const n = toNumber(value)
  if (n === null) return opts?.fallback ?? DEFAULT_FALLBACK

  const { min, max } = resolveDecimals(settings, opts)
  const grouping = opts?.grouping ?? true

  const fixed = toFixedHalfExpand(Math.abs(n), max)
  const [intPart, fracPartRaw = ''] = fixed.split('.')

  const intOut = grouping ? groupInteger(intPart, settings.thousandsSeparator) : intPart
  const fracOut = trimFraction(fracPartRaw, min)

  let out = fracOut ? intOut + settings.decimalSeparator + fracOut : intOut

  // Number(fixed) evita el "-0,00" cuando el valor redondea a cero
  if (n < 0 && Number(fixed) !== 0) out = '-' + out

  return out
}

/**
 * Formatea con código de moneda como sufijo (ej. "1.234,56 USD").
 *
 * Nota: no emite símbolo. Ver riesgo #5 del plan — los sitios con
 * Intl style:'currency' pierden el símbolo al migrar. Sufijo de código es
 * la convención que ya usaba formatWithCurrency.
 */
export function formatCurrency(
  value: number | string | null | undefined,
  settings: FormatSettings,
  opts?: CurrencyFormatOptions
): string {
  const formatted = formatNumber(value, settings, { intent: 'money', ...opts })
  if (formatted === (opts?.fallback ?? DEFAULT_FALLBACK)) return formatted
  return opts?.currency ? `${formatted} ${opts.currency}` : formatted
}

const CURRENCY_SYMBOLS_RE = /[€$£¥₹₽]/g

/**
 * Parseo ESTRICTO: inverso exacto de formatNumber bajo los mismos settings.
 *
 * Para input tecleado por el usuario y para releer strings que nosotros mismos
 * renderizamos. Con separadores explícitos no hay ambigüedad: "300,000" con
 * {thousands:'.', decimal:','} es inequívocamente 300.0.
 *
 * NO usar sobre datos importados ni sobre montos legacy guardados en Firestore
 * como string — esos vienen en formato desconocido. Para eso está
 * @/utils/format/importParsing.
 *
 * Devuelve null en inválido (no 0: convertir basura en cero oculta errores).
 */
export function parseNumber(
  input: string | number | null | undefined,
  settings: FormatSettings
): number | null {
  if (input === null || input === undefined) return null
  if (typeof input === 'number') return Number.isFinite(input) ? input : null

  let s = String(input).trim()
  if (!s) return null

  s = s.replace(CURRENCY_SYMBOLS_RE, '').replace(/\s/g, ' ').trim()

  // Quita el código de moneda que formatCurrency agrega como sufijo
  s = s.replace(/\s*[A-Za-z]{3}$/, '').trim()
  if (!s) return null

  let negative = false
  if (s.startsWith('-')) {
    negative = true
    s = s.slice(1)
  } else if (s.startsWith('+')) {
    s = s.slice(1)
  }

  const { thousandsSeparator, decimalSeparator } = settings

  if (thousandsSeparator) {
    s = s.split(thousandsSeparator).join('')
  }

  // Un solo separador decimal como máximo
  const firstDecimal = s.indexOf(decimalSeparator)
  if (firstDecimal !== -1 && s.indexOf(decimalSeparator, firstDecimal + 1) !== -1) return null
  if (firstDecimal !== -1) {
    s = s.slice(0, firstDecimal) + '.' + s.slice(firstDecimal + 1)
  }

  if (!/^\d*\.?\d*$/.test(s) || s === '' || s === '.') return null

  const n = Number(s)
  if (!Number.isFinite(n)) return null

  return negative ? -n : n
}

/** parseNumber con default. Para los shims deprecados que deben devolver 0. */
export function parseNumberOr(
  input: string | number | null | undefined,
  settings: FormatSettings,
  fallback: number
): number {
  const n = parseNumber(input, settings)
  return n === null ? fallback : n
}

// ---------------------------------------------------------------------------
// Fechas
// ---------------------------------------------------------------------------

const ISO_DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/

interface DateParts {
  day: string
  month: string
  year: string
}

/**
 * Convierte DateLike a partes de fecha.
 *
 * Regla de zona horaria (riesgo #9 del plan): fecha sola en UTC, fecha-hora en local.
 * Un string 'YYYY-MM-DD' se parsea textualmente para no correrse un día.
 */
function toDateParts(value: DateLike): DateParts | null {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'string') {
    const trimmed = value.trim()
    const isoMatch = trimmed.match(ISO_DATE_ONLY_RE)
    if (isoMatch) {
      const [, year, month, day] = isoMatch
      return { day, month, year }
    }
  }

  const d = toDateObject(value)
  if (!d) return null

  return {
    day: String(d.getUTCDate()).padStart(2, '0'),
    month: String(d.getUTCMonth() + 1).padStart(2, '0'),
    year: String(d.getUTCFullYear()),
  }
}

export function toDateObject(value: DateLike): Date | null {
  if (value === null || value === undefined || value === '') return null

  if (value instanceof Date) return isNaN(value.getTime()) ? null : value

  if (typeof value === 'number') {
    const d = new Date(value)
    return isNaN(d.getTime()) ? null : d
  }

  if (typeof value === 'object' && 'seconds' in value && 'nanoseconds' in value) {
    const d = new Date(value.seconds * 1000 + value.nanoseconds / 1e6)
    return isNaN(d.getTime()) ? null : d
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    const isoMatch = trimmed.match(ISO_DATE_ONLY_RE)
    // 'YYYY-MM-DD' -> mediodía UTC, para que getUTC* devuelva el día textual
    if (isoMatch) {
      const [, year, month, day] = isoMatch
      return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12))
    }
    const d = new Date(trimmed)
    return isNaN(d.getTime()) ? null : d
  }

  return null
}

function assembleDate(parts: DateParts, settings: FormatSettings): string {
  const { day, month, year } = parts
  switch (settings.dateFormat) {
    case 'DD/MM/YYYY': return `${day}/${month}/${year}`
    case 'MM/DD/YYYY': return `${month}/${day}/${year}`
    case 'YYYY/MM/DD': return `${year}/${month}/${day}`
    case 'YYYY-MM-DD': return `${year}-${month}-${day}`
    case 'DD-MM-YYYY': return `${day}-${month}-${year}`
    default: return `${day}/${month}/${year}`
  }
}

export function formatDate(
  value: DateLike,
  settings: FormatSettings,
  opts?: DateFormatOptions
): string {
  const parts = toDateParts(value)
  if (!parts) return opts?.fallback ?? DEFAULT_FALLBACK
  return assembleDate(parts, settings)
}

/**
 * Como formatDate, pero toma los componentes de la fecha en zona LOCAL, no UTC.
 *
 * Para los datepickers: el Date representa el día de calendario que el usuario
 * eligió en su zona, así que formatearlo en UTC podría correrlo un día. Es la
 * regla "date-only local" que complementa a formatDate (date-only UTC, para
 * fechas que vienen como string de almacenamiento).
 */
export function formatLocalDate(
  value: DateLike,
  settings: FormatSettings,
  opts?: DateFormatOptions
): string {
  const d = toDateObject(value)
  if (!d) return opts?.fallback ?? DEFAULT_FALLBACK
  const parts: DateParts = {
    day: String(d.getDate()).padStart(2, '0'),
    month: String(d.getMonth() + 1).padStart(2, '0'),
    year: String(d.getFullYear()),
  }
  return assembleDate(parts, settings)
}

/** Hora local (no UTC): un timestamp representa un instante y se muestra en la zona del usuario. */
export function formatTime(
  value: DateLike,
  settings: FormatSettings,
  opts?: DateTimeFormatOptions
): string {
  const d = toDateObject(value)
  if (!d) return opts?.fallback ?? DEFAULT_FALLBACK

  const hours24 = d.getHours()
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const secs = String(d.getSeconds()).padStart(2, '0')
  const tail = opts?.seconds ? `:${secs}` : ''

  if (settings.timeFormat === '12h') {
    const suffix = hours24 < 12 ? 'AM' : 'PM'
    const h12 = hours24 % 12 === 0 ? 12 : hours24 % 12
    return `${String(h12).padStart(2, '0')}:${minutes}${tail} ${suffix}`
  }

  return `${String(hours24).padStart(2, '0')}:${minutes}${tail}`
}

/**
 * Fecha + hora en zona local.
 *
 * A diferencia de formatDate (UTC), aquí la parte de fecha también se toma en
 * local: mostrar "15/07 23:30 UTC" como "16/07 23:30" sería incoherente.
 */
export function formatDateTime(
  value: DateLike,
  settings: FormatSettings,
  opts?: DateTimeFormatOptions
): string {
  const d = toDateObject(value)
  if (!d) return opts?.fallback ?? DEFAULT_FALLBACK

  const localParts: DateParts = {
    day: String(d.getDate()).padStart(2, '0'),
    month: String(d.getMonth() + 1).padStart(2, '0'),
    year: String(d.getFullYear()),
  }

  return `${assembleDate(localParts, settings)} ${formatTime(d, settings, opts)}`
}

/**
 * Parsea una fecha escrita en el formato configurado y devuelve ISO 'YYYY-MM-DD'.
 * Devuelve null si no coincide con el formato o la fecha no existe (ej. 31/02).
 */
export function parseDate(input: string, settings: FormatSettings): string | null {
  if (!input) return null

  const parts = String(input).trim().split(/[/\-.]/)
  if (parts.length !== 3 || parts.some((p) => !/^\d+$/.test(p))) return null

  let day: string, month: string, year: string
  switch (settings.dateFormat) {
    case 'MM/DD/YYYY':
      [month, day, year] = parts
      break
    case 'YYYY/MM/DD':
    case 'YYYY-MM-DD':
      [year, month, day] = parts
      break
    case 'DD/MM/YYYY':
    case 'DD-MM-YYYY':
    default:
      [day, month, year] = parts
  }

  if (year.length !== 4) return null

  const y = Number(year)
  const m = Number(month)
  const d = Number(day)
  if (m < 1 || m > 12 || d < 1 || d > 31) return null

  // Rechaza fechas inexistentes (31/02): el Date se desborda al mes siguiente
  const probe = new Date(Date.UTC(y, m - 1, d, 12))
  if (probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d || probe.getUTCFullYear() !== y) {
    return null
  }

  return `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

/** Convierte cualquier DateLike a 'YYYY-MM-DD' (UTC). Independiente de la config. */
export function formatDateToISO(value: DateLike): string | null {
  const parts = toDateParts(value)
  if (!parts) return null
  return `${parts.year}-${parts.month}-${parts.day}`
}
